import UI from "@mile-meeting/mile-ui";
import { Container, ContentContainer, EmptyView, Line, TabContainer, TabHeaderContainer, TabItem, WorkspaceListContainer } from "./index.styles";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import WorkspaceItem from "../component/workspace-item";
import { WorkspaceItemsContainer } from "../component/workspace-item/index.styles";
import { getUsersMemberList, TUser } from "apis/user/index";
import { accessWorkspace } from "apis/auth/member";
import { encrypt } from "utils/crypto";
import { useRecoilValue } from "recoil";
import { user } from "recoil/recoilState";
import { useToast } from "hook/useToast";
import { logout } from "apis/auth/logout";
import { BtnContainer } from "pages/auth/sign-up/basic-sign-up/index.styles";
import RevokeModal from "./revoke-modal";
import { checkAccountDeletionPermission, revokeUser } from "apis/auth/auth";
import { acceptInviteWorkspace, getMyInviteMember } from "apis/member-invites/member-invites";
import { PendingWorkspaceInfoContainer, PendingWorkspaceItemsContainer } from "../component/pending-workspace-item/index.styles";
import PendingWorkspaceItem from "../component/pending-workspace-item";

enum TabCategory {
  JoinedWorkspaces = "소속된 워크스페이스",
  PendingWorkspaces = "초대된 워크스페이스",
}

const WorkspaceList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [section, setSection] = useState<TabCategory>(TabCategory.JoinedWorkspaces);
  const [workspaces, setWorkspaces] = useState<TUser[]>([]);
  const [pendingWorkspaces, setPendingWorkspaces] = useState<TUser[]>([]);
  const userInfo = useRecoilValue(user);
  const [userName, setUserName] = useState<string>("");
  const [openRevokeModal, setOpenRevokeModal] = useState<boolean>(false);
  const [isDeleted, setIsDeleted] = useState<boolean>(false);

  const onHandleLogout = async () => {
    const res = await logout();

    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  const onHandleRevoke = async () => {
    const res = await checkAccountDeletionPermission();
    if (res.success) {
      if (res.data?.isPossibleToDeleteUser) {
        setOpenRevokeModal(true);
      } else {
        showToast(
          `${res.data?.impossibleToDeleteWorkspaceNames
            .map((name) => `"${name}"`)
            .join(", ")} 워크스페이스의 다른 멤버에게 Owner 권한을 부여한 뒤, 재시도해 주세요.`,
          "warning"
        );
      }
    }
  };

  const onHandleAcceptWorkspace = async (id: string) => {
    const res = await acceptInviteWorkspace(id);
    if (res.success) {
      showToast("워크스페이스 초대가 수락되었습니다.", "success");
      getWorkspaceList();
    }
  }

  const onChangeSection = (section: TabCategory) => {
    //TODO: 재조회 넣어야함.
    setSection(section);
  };

  window.addEventListener("popstate", () => {
    if (isDeleted) {
      window.location.href = "/workspace/list";
    }
  });

  const onHandleCreate = () => {
    //워크스페이스 생성을 클릭했을 때 실행되는 로직입니다.
    window.location.href = "/workspace/create";
  };

  const onHandleAccessWorkspace = async (member: TUser) => {
    //워크스페이스를 접속할 때 실행되는 로직입니다.
    const res = await accessWorkspace(member);
    if (res.success && res.accessToken) {
      if (res.user?.autoLogin) localStorage.setItem("access-token", encrypt(res.accessToken));
      else if (res.user?.autoLogin === false) sessionStorage.setItem("access-token", encrypt(res.accessToken));
      window.location.href = "/";
    } else {
      alert(res.message);
    }
  };

  const getWorkspaceList = () => {
    getUsersMemberList().then((data) => {
      if (data.success && data.members) {
        setWorkspaces(data.members);
      }
    });

    getMyInviteMember().then((data) => {
      if (data.success && data.members) {
        setPendingWorkspaces(data.members);
      }
    });
  }

  useEffect(() => {
    getWorkspaceList();
  }, []);

  useEffect(() => {
    if (location.state?.signupSuccess) {
      showToast("회원가입이 성공적으로 완료되었습니다!", "success");
      window.history.replaceState({}, "");
    }
    if (location.state?.workspaceDeleteSuccess) {
      showToast("워크스페이스가 삭제되었습니다.", "success");
      setIsDeleted(true);
      window.history.replaceState({}, "");
    }
    if (location.state?.exitSuccess) {
      showToast(`"${location.state.workspaceName}" 워크스페이스 나가기에 성공했습니다.`, "success");
      setIsDeleted(true);
      window.history.replaceState({}, "");
    }
  }, [location]);

  useEffect(() => {
    setUserName(userInfo?.name ?? "-");
  }, [userInfo]);

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      {userName && (
        <UI.Navigation.TopNav isShowProfile isShowLogout profileName={userName} onHandleLogout={onHandleLogout} />
      )}
      <Container>
        <WorkspaceListContainer>
          <UI.Typography type="title01" fontStyle="bold">
            워크스페이스 목록
          </UI.Typography>
          <ContentContainer>
            <TabContainer>
              <TabHeaderContainer>
                <TabItem selected={section === TabCategory.JoinedWorkspaces} onClick={() => onChangeSection(TabCategory.JoinedWorkspaces)}>
                  <UI.Typography
                    type="body01 normal"
                    fontStyle="bold"
                    color={
                      section === TabCategory.JoinedWorkspaces ? "var(--alias-color-primary-normal" : "var(--alias-color-label-alternative)"
                    }
                  >
                    소속된 워크스페이스 {workspaces.length > 0 ? <>({workspaces.length})</> : <></>}
                  </UI.Typography>
                </TabItem>
                <TabItem selected={section === TabCategory.PendingWorkspaces} onClick={() => onChangeSection(TabCategory.PendingWorkspaces)}>
                  <UI.Typography
                    type="body01 normal"
                    fontStyle="bold"
                    color={
                      section === TabCategory.PendingWorkspaces
                        ? "var(--alias-color-primary-normal"
                        : "var(--alias-color-label-alternative)"
                    }
                  >
                    초대된 워크스페이스 {pendingWorkspaces.length > 0 ? <>({pendingWorkspaces.length})</> : <></>}
                  </UI.Typography>
                </TabItem>
              </TabHeaderContainer>
              {
                section === TabCategory.JoinedWorkspaces && (<>{
                  workspaces.length === 0 && (
                    <EmptyView>
                      <UI.Icon name="office" size={56} color={"var(--alias-color-label-alternative)"} />
                      <UI.Typography type="headline01" fontStyle="medium" color={"var(--alias-color-label-alternative)"}>
                        소속된 워크스페이스가 없습니다.
                        <br />
                        새로운 워크스페이스를 생성하거나,
                        <br />
                        관리에게 초대 링크를 요청해 보세요.
                      </UI.Typography>
                    </EmptyView>
                  )}
                  <WorkspaceItemsContainer>
                    {workspaces.length > 0 &&
                      workspaces.map((item, index) => (
                        <WorkspaceItem
                          onClick={() => onHandleAccessWorkspace(item)}
                          key={index}
                          workspaceName={item.workspace.name}
                          role={item.role}
                          workspaceImage={item.workspace.image}
                        />
                      ))}
                  </WorkspaceItemsContainer>
                </>)
              }
              {
                section === TabCategory.PendingWorkspaces && (<>
                  {pendingWorkspaces.length === 0 && (
                    <EmptyView>
                      <UI.Icon name="office" size={56} color={"var(--alias-color-label-alternative)"} />
                      <UI.Typography type="headline01" fontStyle="medium" color={"var(--alias-color-label-alternative)"}>
                        초대된 워크스페이스가 없습니다.
                        <br />
                        관리에게 초대 링크를 요청해 보세요.
                      </UI.Typography>
                    </EmptyView>
                  )}
                  {pendingWorkspaces.length > 0 && (
                    <PendingWorkspaceInfoContainer>
                      <UI.Icon name="inform" size={20} color="#FFD666" />
                      <UI.Typography type="label01" fontStyle="regular" color={"var(--alias-color-label-normal)"}>
                        워크스페이스 초대는 수신일로부터 7일 이후 만료됩니다.
                      </UI.Typography>
                    </PendingWorkspaceInfoContainer>
                  )}
                  <PendingWorkspaceItemsContainer>
                    {pendingWorkspaces.length > 0 &&
                      pendingWorkspaces.map((item, index) => (
                        <PendingWorkspaceItem
                          onClick={() => onHandleAcceptWorkspace(item.id)}
                          key={index}
                          workspaceName={item.workspace.name}
                          role={item.role}
                          workspaceImage={item.workspace.image}
                        />
                      ))}
                  </PendingWorkspaceItemsContainer>
                </>)
              }
            </TabContainer>
            <BtnContainer>
              {section === TabCategory.JoinedWorkspaces && (
                <UI.Button
                  onClick={onHandleCreate}
                  title="새로운 워크스페이스 생성"
                  size="large"
                  prefixIcon="addCircle"
                  showPrefixIcon
                  primary={false}
                />
              )}
              <Line />
              <UI.ButtonGroups.Text
                styles={{
                  container: {
                    width: "max-content",
                    margin: "20px auto 0 auto",
                  },
                }}
                onClick={onHandleRevoke}
                title="Mile 회원 탈퇴"
                type="secondary"
                prefixIcon="removeCircle"
                showPrefixIcon
              />
            </BtnContainer>
          </ContentContainer>
        </WorkspaceListContainer>
        <RevokeModal
          modalIsOpen={openRevokeModal}
          closeModal={() => setOpenRevokeModal(false)}
          placeholder={userInfo?.email ?? ""}
          onChange={async (value) => {
            if (value === userInfo?.email) {
              const res = await revokeUser();
              if (res.success) {
                localStorage.clear();
                navigate("/login", { state: { deleteSuccess: true } });
              } else {
                showToast("오류가 발생했습니다. 다시 시도해주세요.", "error");
              }
            }
          }}
          title="회원 탈퇴하시겠습니까?"
          content={
            <p>
              입력창에 계정 이메일을 입력해 주세요.
              <br />
              <span style={{ color: "var(--alias-color-status-negative)" }}>
                * 계정의 모든 데이터가 삭제되며, 복구가 불가능합니다.
              </span>
            </p>
          }
        />
      </Container>
    </div>
  );
};

export default WorkspaceList;
